import React, { useState, useEffect } from "react";
import EMRPreview from "./EMRPreview";
import HeaderUploadComponent from "./HeaderUploadComponent";
import HeaderTemplateComponent from "./HeaderTemplateComponent";
import LogoUploadComponent from "./LogoUploadComponent";
import { jwtDecode } from "jwt-decode";
import {
  updateEMRSettings,
  fetchEMRSettings,
  serviceFetchDoctorList,
} from "../services/api";
import {
  serviceGetClinicBasicDetails,
  serviceGetClinicAddressDetails,
  serviceGetClinicContactDetails,
} from "../services/profileApi";
import { getTokenClinicDoctor } from "../../patient/utils/commonUtils";

const EMRSettings = () => {
  const token = localStorage.getItem("token");
  const decodedToken = jwtDecode(token);
  const [selectedTemplate, setSelectedTemplate] = useState("default");
  const [showQRCode, setShowQRCode] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [headerType, setHeaderType] = useState("template");
  const [headerLayout, setHeaderLayout] = useState("layout1");
  const [uploadedHeaderImage, setUploadedHeaderImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [headerData, setHeaderData] = useState({
    doctorName: "",
    degree: "",
    clinicName: "",
    clinicAddress: "",
    clinicContact: "",
    clinicLogo: null,
  });

  const handleHeaderLayoutChange = (layout) => {
    setHeaderLayout(layout);
  };

  const handleLogoUpload = (file) => {
    setHeaderData((prev) => ({
      ...prev,
      clinicLogo: file,
    }));
  };

  const handleFileUpload = (file) => {
    setUploadedHeaderImage(file);
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    const formData = new FormData();
    formData.append("doctor_id", doctorId);
    formData.append("clinic_id", clinicId);
    formData.append("template_type", selectedTemplate);
    formData.append("header_type", headerType);
    formData.append("header_layout", headerLayout);
    formData.append("header_data", JSON.stringify(headerData));
    formData.append("show_qr_code", showQRCode);
    formData.append("show_signature", showSignature);
    formData.append("margins", JSON.stringify(margins));
    formData.append("font_size", fontSize);
    formData.append(
      "visible_patient_details",
      JSON.stringify(visiblePatientDetails)
    );

    if (headerData.clinicLogo) {
      formData.append("logo", headerData.clinicLogo);
    }

    if (uploadedHeaderImage) {
      formData.append("headerImage", uploadedHeaderImage);
    }

    try {
      const result = await updateEMRSettings(formData);
      console.log("EMR settings saved successfully", result);
    } catch (error) {
      console.error("Error saving EMR settings:", error);
    }
    setIsSaving(false);
  };

  const [margins, setMargins] = useState({
    left: 0.5,
    right: 0.5,
    top: 0.5,
    bottom: 0.5,
  });
  const [fontSize, setFontSize] = useState("regular");

  const [patientDetails, setPatientDetails] = useState({
    name: "Ajay Sharma",
    age: 35,
    id: "12345",
    gender: "Male",
    phone: "+91 98765 43210",
    appointmentDate: new Date(),
  });

  const [visiblePatientDetails, setVisiblePatientDetails] = useState({
    name: true,
    age: true,
    id: true,
    gender: true,
    phone: true,
    appointmentDate: true,
  });

  const toggleDetailVisibility = (detail) => {
    setVisiblePatientDetails((prev) => ({ ...prev, [detail]: !prev[detail] }));
  };

  const { doctorId, clinicId } = getTokenClinicDoctor();

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchEMRSettings(doctorId, clinicId);
        if (response.status) {
          const data = response.data;
          setSelectedTemplate(data.template_type || "default");
          setHeaderType(data.header_type || "template");
          setHeaderLayout(data.header_layout || "layout4");
          setHeaderData(data.header_data || {});
          setShowQRCode(data.show_qr_code || false);
          setShowSignature(data.show_signature || false);
          setMargins(
            data.margins || { left: 0.5, right: 0.5, top: 0.5, bottom: 0.5 }
          );
          setFontSize(data.font_size || "regular");
          setVisiblePatientDetails(data.visible_patient_details || {});

          if (data.uploaded_logo) {
            setHeaderData((prev) => ({
              ...prev,
              clinicLogo: data.uploaded_logo,
            }));
          }

          if (data.uploaded_header_image) {
            setUploadedHeaderImage(data.uploaded_header_image);
          }

          // Set the settings type based on the saved settings
          if (data.settings_type === "default_upload") {
            setSelectedTemplate("default");
            setHeaderType("upload");
          } else if (data.settings_type === "default_template") {
            setSelectedTemplate("default");
            setHeaderType("template");
          } else if (data.settings_type === "letterhead") {
            setSelectedTemplate("letterhead");
          }
        } else {
          console.error("Failed to fetch EMR settings");
        }
      } catch (error) {
        console.error("Error fetching EMR settings:", error);
      }
    };

    const fetchDoctorDetails = async () => {
      try {
        const response = await serviceFetchDoctorList(doctorId, true);
        if (response.status) {
          const doctor = response.data.doctors[0];
          setHeaderData((prev) => ({
            ...prev,
            doctorName: `${doctor.first_name} ${doctor.last_name}`,
            degree: doctor.doctor_qualification,
          }));
        } else {
          console.error("Failed to fetch doctor details");
        }
      } catch (error) {
        console.error("Error fetching doctor details:", error);
      }
    };

    const fetchClinicDetails = async () => {
      try {
        const basicResponse = await serviceGetClinicBasicDetails({ clinicId });
        if (basicResponse.status) {
          setHeaderData((prev) => ({
            ...prev,
            clinicName: basicResponse.data.clinicName,
          }));
        } else {
          console.error("Failed to fetch clinic basic details");
        }

        const addressResponse = await serviceGetClinicAddressDetails({
          userId: decodedToken.user_id,
        });
        if (addressResponse) {
          const { street_1, street_2, city, state, country, pin_code } =
            addressResponse;
          const address = `${street_1}, ${
            street_2 || ""
          }, ${city}, ${state}, ${country}, ${pin_code}`;
          setHeaderData((prev) => ({
            ...prev,
            clinicAddress: address,
          }));
        } else {
          console.error("Failed to fetch clinic address details");
        }

        const contactResponse = await serviceGetClinicContactDetails({
          userId: decodedToken.user_id,
        });
        if (contactResponse.status) {
          setHeaderData((prev) => ({
            ...prev,
            clinicContact:
              contactResponse.data.contactInfo.primary_phone_number,
          }));
        } else {
          console.error("Failed to fetch clinic contact details");
        }
      } catch (error) {
        console.error("Error fetching clinic details:", error);
      }
    };

    if (doctorId && clinicId) {
      loadData();
      fetchDoctorDetails();
      fetchClinicDetails();
    }
  }, [doctorId, clinicId, decodedToken]);

  return (
    <div className="flex flex-col md:flex-row bg-gray-100 min-h-screen">
      <div className="w-full md:w-1/3 bg-white shadow-lg rounded-lg p-8 mb-8 md:mb-0 md:mr-8">
        <h2 className="text-3xl font-bold mb-6 text-indigo-600">
          Print Settings
        </h2>
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Template Type</h3>
          <div className="flex space-x-4">
            <button
              onClick={() => setSelectedTemplate("default")}
              className={`px-4 py-2 rounded-lg font-semibold ${
                selectedTemplate === "default"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Default
            </button>
            <button
              onClick={() => setSelectedTemplate("letterhead")}
              className={`px-4 py-2 rounded-lg font-semibold ${
                selectedTemplate === "letterhead"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Print on Letterhead
            </button>
          </div>
        </div>
        {selectedTemplate === "default" && (
          <>
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4">Header Type</h3>
              <div className="flex space-x-4">
                <button
                  onClick={() => setHeaderType("upload")}
                  className={`px-4 py-2 rounded-lg font-semibold ${
                    headerType === "upload"
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  Upload Header
                </button>
                <button
                  onClick={() => setHeaderType("template")}
                  className={`px-4 py-2 rounded-lg font-semibold ${
                    headerType === "template"
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  Use Template
                </button>
              </div>
              {headerType === "upload" ? (
                <div className="mt-6">
                  <HeaderUploadComponent onFileUpload={handleFileUpload} />
                </div>
              ) : (
                <>
                  <div className="mt-6">
                    <LogoUploadComponent onLogoUpload={handleLogoUpload} />
                  </div>
                  <div className="mt-6">
                    <h3 className="text-xl font-semibold mb-4">
                      Header Layout
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { layout: "layout1", label: "Layout 1" },
                        { layout: "layout2", label: "Layout 2" },
                        { layout: "layout3", label: "Layout 3" },
                        { layout: "layout4", label: "Layout 4" },
                      ].map(({ layout, label }) => (
                        <div
                          key={layout}
                          className={`p-4 border-2 rounded-md cursor-pointer ${
                            headerLayout === layout
                              ? "border-indigo-600 bg-indigo-100"
                              : "border-gray-300"
                          }`}
                          onClick={() => handleHeaderLayoutChange(layout)}
                        >
                          <div className="text-center">{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Additional Settings</h3>
          <div className="space-y-4">
            {[
              {
                label: "Show QR Code on prescription",
                value: showQRCode,
                setter: setShowQRCode,
              },
              {
                label: "Show Signature",
                value: showSignature,
                setter: setShowSignature,
              },
            ].map((item, index) => (
              <div key={index} className="flex items-center">
                <input
                  type="checkbox"
                  checked={item.value}
                  onChange={() => item.setter(!item.value)}
                  className="form-checkbox h-5 w-5 text-indigo-600 mr-2"
                />
                <span className="text-gray-700 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        {selectedTemplate === "letterhead" && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">
              Print Settings Template
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {["left", "right", "top", "bottom"].map((side) => (
                <div key={side} className="flex flex-col">
                  <label className="text-gray-700 font-medium mb-1 capitalize">
                    {side} Margin (cm):
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={5}
                    step={0.1}
                    value={margins[side]}
                    onChange={(e) =>
                      setMargins({
                        ...margins,
                        [side]: parseFloat(e.target.value),
                      })
                    }
                    className="form-input rounded-lg border-gray-300 focus:border-indigo-600 focus:ring focus:ring-indigo-200"
                  />
                </div>
              ))}
            </div>
            <div>
              <span className="block text-gray-700 font-medium mb-2">
                Font Size:
              </span>
              <div className="flex space-x-4">
                {["small", "regular", "medium", "large"].map((size) => (
                  <label key={size} className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="fontSize"
                      value={size}
                      checked={fontSize === size}
                      onChange={() => setFontSize(size)}
                      className="form-radio text-indigo-600"
                    />
                    <span className="text-gray-700 font-medium capitalize">
                      {size}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4">Patient Details</h3>
          <div className="grid grid-cols-2 gap-4">
            {Object.keys(patientDetails).map((detail) => (
              <div key={detail} className="flex items-center">
                <input
                  type="checkbox"
                  checked={visiblePatientDetails[detail]}
                  onChange={() => toggleDetailVisibility(detail)}
                  className="form-checkbox h-5 w-5 text-indigo-600 mr-2"
                />
                <span className="text-gray-700 font-medium capitalize">
                  {detail.replace(/([A-Z])/g, " $1").trim()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full md:w-2/3 bg-white shadow-lg rounded-lg p-8">
        <h2 className="text-3xl font-bold mb-6 text-indigo-600">
          Live HTML Preview
        </h2>
        <EMRPreview
          showQRCode={showQRCode}
          selectedTemplate={selectedTemplate}
          showSignature={showSignature}
          patientDetails={Object.keys(patientDetails).reduce((acc, key) => {
            if (visiblePatientDetails[key]) {
              acc[key] = patientDetails[key];
            }
            return acc;
          }, {})}
          margins={margins}
          fontSize={fontSize}
          headerType={headerType}
          headerData={headerData}
          headerLayout={headerLayout}
          uploadedHeaderImage={uploadedHeaderImage}
        />
      </div>
      <div className="fixed bottom-8 right-8">
        <button
          className={`px-6 py-3 rounded-lg font-semibold ${
            isSaving
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
          } text-white`}
          onClick={handleSaveSettings}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

export default EMRSettings;
